import { defineStore } from 'pinia'
import {getCookie, setCookie} from "@/utils/cookies";
import {dateToString, makeDateFromString} from "@/utils/dateUtils";
import {areAddressesSame} from "@/utils/compareAddress";
import { fetchWrapper } from '@/utils/fetchWrapper';
import { hasStaleExpressRates } from '@/utils/shippingRates';
import * as Sentry from '@sentry/vue';

const prefix ="https://" + window.location.host;
const generateURLParams = (params: Record<string, string>) => {
  return Object.keys(params).map(key => encodeURIComponent(key) + '=' + encodeURIComponent(params[key]) ).join('&');
}

const htmlDecode = (input: string): string => {
  const e = document.createElement('textarea');
  e.innerHTML = input;

  return e.childNodes.length === 0 ? "" : e.childNodes[0].nodeValue || "";
}

// We don't ship to military addresses (Armed Forces Americas/Europe/Pacific)
export const militaryStates = ["AA", "AE", "AP"];

const filterAddress = (address: Address | AddressFilled) => {
  return address.address1 && address.city && address.country && address.country_code && address.province_code && !militaryStates.includes(address.province_code) && address.first_name && address.last_name
}

export interface Address {
  id: string;
  address1: string;
  address2: string;
  city: string;
  company: string;
  country: string;
  country_code: string;
  province: string;
  province_code: string;
  first_name: string;
  last_name: string;
  zip: string;
  phone: string;
}

export interface AddressFilled extends Address {
  validation_status: string;
  isNew?: boolean;
  type: string;
}

export interface Country {
  code: string;
  name: string;
  hide_from_signup: boolean;
  provinces: {
    code: string;
    name: string;
  }[];
}

export interface DiscountData {
  code: string;
  success: boolean;
  error_code?: string;
  message?: string;
  totals?: {
    subtotal: number;
    shipping: number;
    tax: number;
    discount: number;
    grandTotal: number;
  };
}

interface LocalStorageState {
  customer: Customer
  discountData: Omit<DiscountData, 'error_code'>;
  localLineItems: {
    [key: string]: LocalLineItem
  };
}

interface ShopifyLineItem {
  discounted_price: number;
  discounts: [];
  featured_image: {
    aspect_ratio: number;
    alt: string;
    height: number;
    url: string;
    width: number;
  };
  final_line_price: number;
  final_price: number;
  gift_card: boolean;
  grams: number;
  handle: string;
  id: number;
  image: string;
  key: string;
  line_level_discount_allocations: [];
  line_level_total_discount: number;
  line_price: number;
  options_with_values: {name: string, value: string}[];
  original_line_price: number;
  original_price: number;
  price: number;
  product_description: string;
  product_has_only_default_variant: boolean;
  product_id: number;
  product_title: string;
  product_type: string;
  properties: {
    [key: string]: string;
  };
  quantity: number;
  requires_shipping: boolean;
  sku: string;
  taxable: boolean;
  title: string;
  total_discount: number;
  url: string;
  variant_id: number;
  variant_options: string[];
  variant_title: string;
  vendor: string;
  weight: number;
  compare_at_price: number | null;
  min_date: Date | null;
}

interface LocalLineItem {
  quantity: number;
  token: string | null;
  rates: any[];
  selectedAddress: AddressFilled | null;
  selectedRate: any | null;
  confirmed: boolean;
  editStep: number | null;
  errors: {
    address: boolean;
    date: boolean;
  }
  properties: {
    [key: string]: string;
  }
}

interface Customer {
  id: number | null;
  email: string;
  addresses: Address[];
  isAuthorized: boolean;
}

interface ShopifyCartAdditionalData {
  cart: Omit<ShopifyCart, 'token'>;
  product_variants: Array<{
    key: string;
    price: number;
    compare_at_price: number | null;
    weight: number;
    min_date: string;
  }>;
  cutoff_time: string;
  max_date: string;
  disabled_shipping_dates: string | null;
  disabled_delivery_dates: string | null;
  disable_weekends: string;
  customer: Customer;
}

interface ShopifyCart {
  attributes: {
    [key: string]: string;
  };
  cart_level_discount_applications: [];
  currency: string;
  item_count: number;
  items: ShopifyLineItem[];
  items_subtotal_price: number;
  note: string;
  original_total_price: number;
  token: string;
  requires_shipping: boolean;
  total_discount: number;
  total_price: number;
  total_weight: number;
}

export interface ShippingRate {
  accepts_instructions: boolean;
  additional_data: null | string;
  api_client_id: number;
  carrier_identifier: string;
  carrier_service_id: number;
  charge_items: null | string[];
  code: string;
  compare_price: number | null;
  currency: string;
  delivery_category: null | string;
  delivery_date: string;
  delivery_days: number[];
  delivery_range?: string[];
  description: string;
  has_restrictions: any;
  incoterm: any;
  markup: string;
  name: string;
  phone_required: boolean;
  presentment_name: string;
  price: string;
  rating_classification: any;
  requested_fulfillment_service_id: any;
  shipment_options: any;
  source: string;
  using_merchant_account: any;
}

export interface CartState {
  submitting: boolean;
  isDebug: boolean;
  apiUrl: string;
  loqateKey: string;
  useSameShippingAddress: null | string;
  loading: boolean;
  addresses: AddressFilled[];
  countries: Country[];
  discountData: DiscountData;
  cart: ShopifyCart | null;
  cutoff_time: string;
  max_date: Date | null;
  disabled_shipping_dates: Date[];
  disabled_delivery_dates: Date[];
  customer: Customer;
  cartToken: string | null;
  localLineItems: {
    [key: string]: LocalLineItem
  }
}

declare global {
  interface Window {
    Shopify: {
      shop: string;
      routes: {
        root: string;
        cart: string;
      }
    };
  }
}

interface Package {
  delivery_date: string;
  items: {
    product_id: number;
    properties: {
      [key: string]: string;
    };
    qty: number;
    variant_id: number;
  }[];
  ship_date: string;
  shipping_address: {
    firstname: string;
    lastname: string;
    address1: string;
    address2: string;
    city: string;
    company: string;
    country: string;
    phone: string;
    state: string;
    validation_status: string;
    zip: string;
  } | null;
  shipping_rate: {
    code: string;
    days: number;
    price: number;
    title: string;
  };
}

export interface LineItem extends ShopifyLineItem, LocalLineItem {}

export interface LineItemWithAddressAndRates extends LineItem {
  selectedAddress: AddressFilled;
  rates: ShippingRate[];
}

export const useCartStore = defineStore('cart', {

  state: (): CartState => ({
    submitting: false,
    isDebug: new URL(window.location.href).searchParams.get('debug') === "1",
    apiUrl: window.Shopify.shop === "c-kruger-dev.myshopify.com" ? "https://api-dev.ckruegers.com/" : "https://api.ckruegers.com/",
    loqateKey: 'YF63-XH86-HN63-PU79',
    useSameShippingAddress: null,
    loading: false,
    addresses: [],
    countries: [],
    discountData: {
      code: '',
      error_code: '',
      success: false
    },
    cart: null,
    cutoff_time: "",
    max_date: null,
    disabled_shipping_dates: [],
    disabled_delivery_dates: [],
    customer: {
      id: null,
      email: "",
      addresses: [],
      isAuthorized: false
    },
    cartToken: null,
    localLineItems: {}
  }),
  getters: {
    isLineItemsFilled(): boolean {
      const lineItems: LineItem[]  = this.lineItems;

      let allPrevIsFilled = true;
      for(const item of lineItems ) {
        if(!item.confirmed || item.editStep) {
          allPrevIsFilled = false;
        }
      }
      return allPrevIsFilled;
    },
    lineItems: (state): (ShopifyLineItem & LocalLineItem)[] => {
      if(!state.cart) {
        return [];
      }

      return state.cart.items.map(item => {
        const localLineItem = state.localLineItems[item.key];

        return {
          ...item,
          ...localLineItem,
          properties: {
            ...item.properties,
            ...localLineItem.properties,
          },
        }
      })
    },
    cartTotal(state): number {
      if(state.discountData && state.discountData.success && state.discountData.totals && state.discountData.totals.grandTotal) {
        return state.discountData.totals.grandTotal + this.cartShipping;
      }
      if(!state.cart) {
        return 0;
      }

      return state.cart.total_price / 100  + this.cartShipping;
    },
    cartSubtotal(state): number {
      if(state.discountData && state.discountData.success && state.discountData.totals && state.discountData.totals.subtotal) {
        return state.discountData.totals.subtotal;
      }
      if(!state.cart) {
        return 0;
      }
      return state.cart.items_subtotal_price / 100
    },
    cartDiscount: (state): number => {
      if(state.discountData && state.discountData.success && state.discountData.totals && state.discountData.totals.discount) {
        return state.discountData.totals.discount;
      }
      return 0;
    },
    cartShipping(state): number {
      const lineItems: (ShopifyLineItem & LocalLineItem)[] = this.lineItems;

      let shippingAmount = lineItems.reduce((sum, i) => sum += parseFloat( (i.selectedRate && i.selectedRate.price || 0) ), 0);

      if(state.discountData && state.discountData.success && state.discountData.totals && state.discountData.totals.shipping) {
        return state.discountData.totals.shipping;
      }

      return shippingAmount;
    },
    packages(): Package[] {
      return this.lineItems.map((lineItem: LineItem) => {
        const properties = { ... lineItem.properties };
        if(properties.hasOwnProperty('_gift_first_name')) {
          delete properties._gift_first_name;
        }
        if(properties.hasOwnProperty('_gift_last_name')) {
          delete properties._gift_last_name;
        }
        if(properties._gift_type === '1') {
          if(properties.hasOwnProperty('To')) {
            delete properties.To;
          }
        }
        if(properties.hasOwnProperty('_gift_type')) {
          delete properties._gift_type;
        }
        if(properties.hasOwnProperty('_count')) {
          delete properties._count;
        }
        if(properties.Message === "") {
          delete properties.Message;
        }
        if(properties.From === "") {
          delete properties.From;
        }

        properties._product_image = lineItem.image.replace('//','https://');

        properties._selected_shipping_rate = JSON.stringify(lineItem.selectedRate);

        return {
          delivery_date: lineItem.selectedRate?.estimatedDate ? Array.isArray(lineItem.selectedRate.estimatedDate) ? dateToString(lineItem.selectedRate.estimatedDate[0]) + " to " + dateToString(lineItem.selectedRate.estimatedDate[1]) : dateToString(lineItem.selectedRate.estimatedDate) : "",
          items: [
            {
              product_id: lineItem.product_id,
              properties: properties,
              qty: lineItem.quantity,
              variant_id: lineItem.variant_id
            }
          ],
          ship_date: lineItem.selectedRate?.shipDate ? dateToString(lineItem.selectedRate?.shipDate) : "",
          shipping_address: lineItem.selectedAddress ?{
            firstname: lineItem.selectedAddress.first_name,
            lastname: lineItem.selectedAddress.last_name,
            address1: lineItem.selectedAddress.address1,
            address2: lineItem.selectedAddress.address2 || "",
            city: lineItem.selectedAddress.city,
            company: lineItem.selectedAddress.company,
            country: lineItem.selectedAddress.country_code,
            phone: lineItem.selectedAddress.phone || "",
            state: lineItem.selectedAddress.province_code,
            validation_status: lineItem.selectedAddress?.validation_status,
            zip: lineItem.selectedAddress.zip,
          } : null,
          shipping_rate: {
            code: lineItem.selectedRate?.code,
            days: lineItem.selectedRate?.delivery_days,
            price: lineItem.selectedRate?.price,
            title: lineItem.selectedRate?.presentment_name
          },
        }
      });
    }
  },
  actions: {
    async getCart(isConfigLoad = false) {
      this.loading = true;

      if(!this.cartToken) {
        const data = await fetchWrapper<ShopifyCart>('/cart.json', {cache: "no-cache"}, { context: 'cartToken', state: JSON.stringify(this.lineItems) });
        this.cartToken = data.token;
      }

      const data = await fetchWrapper<ShopifyCartAdditionalData>('/cart?view=json', {cache: "no-cache"}, { context: 'cart?view=json', state: JSON.stringify(this.lineItems) });

      const cart = data.cart;
      const product_variants: {
        key: string,
        price: number,
        compare_at_price: number | null,
        min_date: string,
        weight: number
      }[] = data.product_variants;

      if(!cart.items.length) {
        window.location.href = '/cart'
      }

      cart.items.reverse();

      cart.items.forEach((lineItem) => {
        const lineItemAdditional = product_variants.find(i => i.key === lineItem.key);
        if(!this.localLineItems[lineItem.key]) {
          this.localLineItems[lineItem.key] = {
            quantity: lineItem.quantity,
            confirmed: false,
            editStep: null,
            errors: {
              address: false,
              date: false,
            },
            properties: {},
            token: null,
            rates: [],
            selectedAddress: null,
            selectedRate: null
          }
        }

        if(lineItemAdditional) {
          const item = cart.items.find(i => i.key === lineItem.key);
          if (item) {
            item.min_date = lineItemAdditional.min_date ? makeDateFromString(lineItemAdditional.min_date) : null;
            item.compare_at_price = lineItemAdditional.compare_at_price;
            item.weight = lineItemAdditional.weight;
          }
        }
      });

      this.cart = {
        ...cart,
        token: ""
      };

      for(const [key] of Object.entries(this.localLineItems)) {
        if(this.cart.items.findIndex(i => i.key === key) === -1) {
          delete this.localLineItems[key];
        }
      }

      if(isConfigLoad) {
        this.cutoff_time = data.cutoff_time;
        this.max_date = data.max_date ? makeDateFromString(data.max_date) : null;

        this.disabled_shipping_dates = (data.disabled_shipping_dates || "").split('\r\n').filter(Boolean).map((i: string) => makeDateFromString(i));
        this.disabled_delivery_dates = (data.disabled_delivery_dates || "").split('\r\n').filter(Boolean).map((i: string) => makeDateFromString(i));

        if(data.customer.email) {
          this.customer = {
            id: data.customer.id,
            email: data.customer.email,
            isAuthorized: true,
            addresses: data.customer.addresses
          }
        } else {
          this.customer = {
            id: null,
            email: "",
            isAuthorized: false,
            addresses: []
          };
        }

        const userAddresses = (data.customer?.addresses || []).filter(filterAddress).map((i: Address) => {
          for (let [key, value] of Object.entries(i) as [keyof Address, string][]) {
            i[key] = htmlDecode(value);
          }

          return i;
        });

        const localStorageSavedAddresses: AddressFilled[] = JSON.parse(window.localStorage.getItem('_cke-addresses-vue') || "[]");

        const savedAddresses = localStorageSavedAddresses.filter(filterAddress).filter((sa) => !userAddresses.some(ca => areAddressesSame(ca, sa) ));

        this.addresses = [
          ...userAddresses.map(i => ({
            ...i,
            validation_status: "",
            type: "",
          })),
          ...savedAddresses,
        ];


        for(const line_item of this.cart.items) {
          const lineItem = this.localLineItems[line_item.key];

          if(lineItem.selectedAddress) {
            await this.getRates(line_item.key);
            //Update user address after being on checkout
            const localSavedAddress = localStorageSavedAddresses.find(i => i.id === lineItem.selectedAddress?.id);
            if(localSavedAddress) {
              const userSavedAddress = userAddresses.find(ca => areAddressesSame(ca, localSavedAddress) );
              if(userSavedAddress) {
                lineItem.selectedAddress = {
                  ...userSavedAddress,
                  validation_status: "",
                  type: "",
                }
              }

            }
          }

          if(lineItem.selectedAddress && this.addresses.findIndex(i=>i.id === lineItem.selectedAddress?.id) === -1) {
            lineItem.selectedAddress = null;
            lineItem.selectedRate = null;
            lineItem.editStep = null;
            lineItem.confirmed = false;
          }
        }
      }

      if(this.discountData.code && this.discountData.success) {
        await this.submitDiscountCode(this.discountData.code);
      }

      this.loading = false;
    },
    async loadState() {
      const lastState = JSON.parse(window.localStorage.getItem('_cke-state-vue') || "{}") as LocalStorageState;

      if(!lastState) {
        return;
      }

      if(lastState.localLineItems) {

        for( const lineItem of Object.values(lastState.localLineItems) ) {
          if(lineItem.selectedRate) {
            if(Array.isArray(lineItem.selectedRate.estimatedDate)) {
              lineItem.selectedRate.estimatedDate = [
                new Date(lineItem.selectedRate.estimatedDate[0]),
                new Date(lineItem.selectedRate.estimatedDate[1])
              ];
            } else {
              lineItem.selectedRate.estimatedDate = new Date(lineItem.selectedRate.estimatedDate);
            }

            lineItem.selectedRate.shipDate = new Date(lineItem.selectedRate.shipDate);
          }
          if(!lineItem.selectedAddress) {
            lineItem.confirmed = false;
            lineItem.selectedRate = null;
          }
          lineItem.errors = {
            ...{
              address: false,
              date: false,
            },
            ...lineItem.errors
          }
        }

        this.localLineItems = lastState.localLineItems;
      }

      if(lastState.customer) {
        this.customer = lastState.customer;
      }

      if(lastState.discountData && lastState.discountData.code && lastState.discountData.success) {
        this.discountData = lastState.discountData;
      }
    },
    async saveAddress( address: AddressFilled, key: string ) {
      const changeAddressIndex = this.addresses.findIndex(i => i.id === address.id);

      if (this.customer.id && !address.isNew) {
        const addressToSave = {
          id: address.id,
          address1: address.address1,
          address2: address.address2,
          city: address.city,
          company: address.company,
          country: address.country,
          country_code: address.country_code,
          firstname: address.first_name,
          lastname: address.last_name,
          phone: address.phone,
          state: address.province_code,
          validation_status: address.validation_status,
          zip: address.zip,
        };

        await fetchWrapper<any>(`${this.apiUrl}address/update`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            customer: {
              id: this.customer.id
            },
            shipping_address: addressToSave
          }),
        }, { context: 'saveAddress', state: JSON.stringify(this.lineItems) });

        this.addresses[changeAddressIndex] = address;
      } else {
        if(changeAddressIndex !== -1) {
          this.addresses[changeAddressIndex] = address;
        } else {
          this.addresses.push(address);
        }
      }

      this.saveAddresses();
      await this.selectAddress(address.id, key);
    },
    saveAddresses() {
      //Save to storage only none customer addresses
      window.localStorage.setItem('_cke-addresses-vue', JSON.stringify(this.addresses.map(i=>i).filter(i=> !this.customer.addresses.some(ci => ci.id === i.id) )));
    },
    async removeAddress( id: string ) {
      if (this.customer.isAuthorized) {
        const shippingAddress = this.addresses.find(i => i.id === id);
        const data = await fetchWrapper<any>(`${this.apiUrl}address/delete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            shipping_address: shippingAddress,
            customer: this.customer
          }),
        }, { context: 'removeAddress', state: JSON.stringify(this.lineItems) });
        if (data.success) {
          this.addresses = this.addresses.filter(i => i.id !== id);
          this.saveAddresses();
        }
      } else {
        this.addresses = this.addresses.filter(i => i.id !== id);
        this.saveAddresses();
      }
      this.persistToLocalStorage();
    },
    async applyAddressToAllShipments( key: string ) {
      const lineAddress = this.localLineItems[key].selectedAddress;

      this.loading = true;
      for (const lineKey in this.localLineItems) {
        if(!this.localLineItems[lineKey].selectedAddress || this.localLineItems[lineKey].selectedAddress.id !== lineAddress?.id) {
          this.localLineItems[lineKey].selectedAddress = lineAddress;
          this.localLineItems[lineKey].rates = [];
          this.localLineItems[lineKey].selectedRate = null;
          this.localLineItems[lineKey].editStep = null;
          this.localLineItems[lineKey].confirmed = false;

          await this.getRates(lineKey);
        }
      }
      this.loading = false;

      this.useSameShippingAddress = key;
      this.persistToLocalStorage();
    },
    async applyGiftMessageToAllShipments(key: string) {
      const lineMessage = this.localLineItems[key].properties.Message;

      this.loading = true;
      for (const lineKey in this.localLineItems) {
        if(!this.localLineItems[lineKey].properties) {
          this.localLineItems[lineKey].properties = {
            Message: lineMessage
          }
        } else {
          this.localLineItems[lineKey].properties.Message = lineMessage;
        }
      }
      this.loading = false;

      this.persistToLocalStorage();
    },
    async selectAddress( addressId: string, key: string ) {
      if(!this.cart) {
        return;
      }
      const lineItem = this.cart.items.find((i) => i.key === key);
      this.loading = true;

      const selectedAddress = this.addresses.find(i => i.id === addressId);

      if(!selectedAddress) {
        this.localLineItems[key].selectedAddress = null;
        this.loading = false;
        return;
      }

      this.localLineItems[key].selectedRate = null;
      this.localLineItems[key].editStep = null;
      this.localLineItems[key].confirmed = false;

      try{
        await fetch('https://api.addressy.com/Capture/Interactive/Find/v1.10/json3.ws', {
          method: 'POST',
          mode: "cors",
          cache: "no-cache",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: generateURLParams({
            Key: this.loqateKey,
            Text: selectedAddress.province_code + " " + selectedAddress.city + " " + selectedAddress.address1 + " " + (selectedAddress.address2 ? selectedAddress.address2 + " " : "") + (selectedAddress.zip.includes('-') ? selectedAddress.zip.split('-')[0] : selectedAddress.zip),
            Country: "US",
            Limit: "1",
            IsMiddleware: "true",
            Container: ""
          })
        })
          .then(response => response.json())
          .then(data => {
            if(data && data.Items && data.Items[0].Id) {
              return fetch('https://services.postcodeanywhere.co.uk/Capture/Interactive/Retrieve/v1.00/json3.ws', {
                method: 'POST',
                mode: "cors",
                cache: "no-cache",
                headers: {
                  "Content-Type": "application/x-www-form-urlencoded",
                },
                body: generateURLParams({
                  Key: this.loqateKey,
                  Id: data.Items[0].Id,
                }),
              });
            }
          })
          .then(response => response?.json())
          .then((data) => {
            selectedAddress.type = data.Items[0]?.Type || 'Commercial';
          });
      } catch(error) {
        Sentry.captureException(error, {
          tags: {
            'status': JSON.stringify(error),
          },
          extra: {
            context: 'selectAddress get type',
            state: JSON.stringify(this.lineItems)
          }
        });
      }

      if(this.useSameShippingAddress) {
        for(const lineItem of this.cart.items) {
          this.localLineItems[lineItem.key].selectedAddress = selectedAddress;
          await this.getRates(lineItem.key);
        }
      } else {
        this.localLineItems[key].selectedAddress = selectedAddress;
        await this.getRates(key);
      }

      this.localLineItems[key].errors = this.localLineItems[key].errors || {}
      this.localLineItems[key].errors.address = false;

      this.loading = false;
    },
    async getRates( key: string, staleRetryDone = false ): Promise<void> {
      this.loading = true;
      await this.getTokenForLineItem(key);
      const selectedAddress = this.localLineItems[key].selectedAddress;

      if(!selectedAddress || !this.localLineItems[key].token || !this.cartToken) {
        return;
      }

      const params = {
        'shipping_address[zip]': selectedAddress.zip,
        'shipping_address[country]': selectedAddress.country,
        'shipping_address[province]': selectedAddress.province_code,
        'shipping_address[city]': selectedAddress.city,
        'shipping_address[address1]': selectedAddress.address1,
        'shipping_address[address2]': selectedAddress.address2,
        'shipping_address[phone]': selectedAddress.phone,
        'shipping_address[name]': selectedAddress.first_name + ' ' + selectedAddress.last_name,
      };

      try {
        setCookie('cart', this.localLineItems[key].token, 14);
        await fetchWrapper<any>(`/cart/prepare_shipping_rates.json?${new URLSearchParams(params).toString()}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }, { context: 'getRates', state: JSON.stringify(this.lineItems) });

        await new Promise(resolve => setTimeout(resolve, 1000));

        // const data = await fetchWrapper<any>(`/cart/shipping_rates.json?${new URLSearchParams(params).toString()}`, {}, { context: 'getRates', state: JSON.stringify(this.lineItems) });
        const data = await fetchWrapper<any>(`/cart/async_shipping_rates.json?${new URLSearchParams(params).toString()}`, {}, { context: 'getRates', state: JSON.stringify(this.lineItems) });

        if(data === null) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          return await this.getRates(key, staleRetryDone);
        }
        setCookie('cart', this.cartToken, 14);

        this.localLineItems[key].rates = data.shipping_rates || [];

        if(!staleRetryDone && hasStaleExpressRates(this.localLineItems[key].rates)) {
          Sentry.captureMessage('Stale express shipping rates received', {
            level: 'warning',
            extra: {
              key,
              zip: selectedAddress.zip,
              rates: this.localLineItems[key].rates.map((rate: ShippingRate) => ({
                name: rate.name,
                code: rate.code,
                delivery_days: rate.delivery_days,
                delivery_date: rate.delivery_date,
                delivery_range: rate.delivery_range,
              })),
            },
          });

          return await this.getRates(key, true);
        }
      } catch (e) {
        setCookie('cart', this.cartToken, 14);
      }

      this.persistToLocalStorage();
      this.loading = false;
    },
    async getCountries() {
      if (!localStorage.getItem('_cke-countries-vue')) {
        const json = await fetchWrapper<any>(prefix + '/services/countries.json', {}, { context: 'getCountries', state: JSON.stringify(this.lineItems) });
        localStorage.setItem('_cke-countries', JSON.stringify(json));
        this.countries = json;
      } else {
        const json = JSON.parse(localStorage.getItem('_cke-countries-vue') || "[]");
        if(!json.length) {
          localStorage.removeItem('_cke-countries-vue');

          await this.getCountries();
        }
        this.countries = json;
      }

      this.persistToLocalStorage();
    },
    async getTokenForLineItem( key: string ) {
      if(!this.cart || !this.cartToken) {
        return;
      }

      const lineItemNew = this.cart.items.find(i => i.key === key);
      const lineItem = this.localLineItems[key];

      if(lineItemNew) {
        if(lineItem.quantity !== lineItemNew.quantity) {
          this.localLineItems[key].quantity = lineItemNew.quantity;
          this.localLineItems[key].confirmed = false;
          this.localLineItems[key].selectedRate = false;
        }

        setCookie('cart', '', -1);

        await fetchWrapper<any>('/cart/add.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [{
              id: lineItemNew.variant_id,
              quantity: lineItemNew.quantity
            }],
          }),
        }, { context: 'getTokenForLineItem', state: JSON.stringify(this.lineItems) });

        this.localLineItems[key].token = getCookie('cart');

        setCookie('cart', this.cartToken, 14);
      }
    },
    async removeProduct( key:string ) {

      await fetchWrapper(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: 0,
        })
      }, { context: 'removeProduct', state: JSON.stringify(this.lineItems) });

      await this.getCart();
      this.persistToLocalStorage();
    },
    async duplicateProduct( key:string ){
      if(!this.cart) {
        return;
      }

      const referenceProduct = this.cart.items.find(i => i.key === key);

      if(!referenceProduct) {
        return;
      }

      const conditionProperties = {} as LineItem['properties'];

      if(this.useSameShippingAddress) {
        conditionProperties._address = this.useSameShippingAddress;
      }

      await fetchWrapper(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: referenceProduct.variant_id,
              properties: {
                ...referenceProduct.properties,
                "_selected_address": "",
                "_date": Date.now(),
                ...conditionProperties
              },
              quantity: 1,
            }
          ]
        })
      }, { context: 'duplicateProduct', state: JSON.stringify(this.lineItems) });

      await this.getCart();
      this.persistToLocalStorage();
    },
    async provideDiscountCodeEmail(email: string) {
      const data = await fetchWrapper<any>(`${this.apiUrl}checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          packages: this.packages,
          customer: this.customer.email ? this.customer : { email },
          is_auto_applying: false,
          coupon_code: this.discountData.code,
          totals: 1
        }),
      }, { context: 'provideDiscountCodeEmail', state: JSON.stringify(this.lineItems) });
      if(data.success) {
        this.discountData.success = true;
        this.discountData.error_code = data.error_code;

        if (data.totals.discount > 0) {
          this.customer.email = email;
          this.discountData.totals = data.totals;
          this.persistToLocalStorage();
        } else {
          this.discountData.code = '';
          this.discountData.success = false;
          this.discountData.error_code = data.error_code || 'opc_4';
          this.discountData.message = 'Discount code is invalid or does not apply to items in your cart.';
        }
      } else {
        this.discountData.code = '';
        this.discountData.success = false;
        this.discountData.error_code = data.error_code || 'opc_4';
        this.discountData.message = 'Discount code is invalid or does not apply to items in your cart.';
      }
    },
    async removeDiscountCode() {
      this.discountData.code = '';
      this.discountData.error_code = '';
      this.discountData.success = false;
      this.persistToLocalStorage();
    },
    async submitDiscountCode( discountCode: string ) {
      this.discountData.code = discountCode;
      this.discountData.error_code = '';
      this.discountData.message = '';
      this.discountData.success = false;
      this.discountData.totals = undefined;

      const data = await fetchWrapper<any>(`${this.apiUrl}checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          packages: this.packages,
          customer: this.customer.isAuthorized && this.customer.email ? this.customer : {},
          coupon_code: discountCode,
          is_auto_applying: false,
          totals: 1
        }),
      }, { context: 'submitDiscountCode', state: JSON.stringify(this.lineItems) });

      if(!data.success) {
        this.discountData.error_code = data.error_code;
        this.discountData.message = data.message;

        return false;
      }

      if(data.totals.discount === 0) {
        this.discountData.error_code = 'opc_4';
        this.discountData.message = 'Discount code is invalid or does not apply to items in your cart.';
      } else {
        this.discountData.success = true;
        this.discountData.error_code = data.error_code;
        this.discountData.message = data.message;
        this.discountData.totals = data.totals;
      }
      this.persistToLocalStorage();

      return true;
    },
    saveEmail( email: string ) {
      this.customer.email = email;
      this.persistToLocalStorage();
    },
    saveGiftMessage({ key, _gift_first_name, _gift_last_name, _gift_type, _gift_sender, Message }: { key: string, _gift_first_name: string, _gift_last_name: string, _gift_type: string, _gift_sender: string, Message: string }) {
      if(!this.localLineItems[key].properties) {
        this.localLineItems[key].properties = {};
      }
      this.localLineItems[key].properties._gift_type = _gift_type;
      this.localLineItems[key].properties._gift_first_name = _gift_first_name;
      this.localLineItems[key].properties._gift_last_name = _gift_last_name;
      this.localLineItems[key].properties.From = _gift_sender;
      this.localLineItems[key].properties.Message = Message
      this.localLineItems[key].properties.To = _gift_first_name ? _gift_first_name+' '+_gift_last_name : _gift_last_name;
      this.persistToLocalStorage();
    },
    saveShipping({ shipDate, estimatedDate, key, rate, isExactDayDelivery, clickedDate }: { shipDate: Date, estimatedDate: Date | [Date, Date], key: string, rate: ShippingRate, isExactDayDelivery: boolean, clickedDate: string }) {
      this.localLineItems[key].errors = this.localLineItems[key].errors || {}
      this.localLineItems[key].errors.date = false;

      this.localLineItems[key].selectedRate = {
        ...rate,
        isExactDayDelivery,
        estimatedDate,
        shipDate,
        clickedDate
      }
      this.persistToLocalStorage();
    },
    setErrors( key: string ) {
      this.localLineItems[key].errors = this.localLineItems[key].errors || {}
      this.localLineItems[key].errors = {
        address: !Boolean(this.localLineItems[key].selectedAddress),
        date: !Boolean(this.localLineItems[key].selectedRate?.shipDate),
      }
    },
    confirmAddedInfo( key: string, useSameAddress: boolean, useSameGiftMessage: boolean ) {
      this.localLineItems[key].editStep = null;
      this.localLineItems[key].confirmed = true;
      if(useSameAddress) {
        this.applyAddressToAllShipments(key);
      }
      if(useSameGiftMessage) {
        this.applyGiftMessageToAllShipments(key);
      }
      this.persistToLocalStorage();
    },
    editStep({ key, step }: { key: string, step: number }) {
      this.localLineItems[key].editStep = step;
    },
    async createDraftOrderAndRedirect() {
      this.submitting = true;

      const data = await fetchWrapper<any>(`${this.apiUrl}checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          packages: this.packages,
          customer: this.customer,
          coupon_code: this.discountData.code
        }),
      }, { context: 'createDraftOrderAndRedirect', state: JSON.stringify(this.lineItems) });

      if (data.success) {
        window.location.href = data.draft_order.invoice_url;
      } else if (data.errors) {
        //TODO error if not created
        //this.errors = data.errors;
      }

      this.loading = false;
    },
    clearRates(key: string) {
      this.localLineItems[key].editStep = null;
      this.localLineItems[key].confirmed = false;
      this.localLineItems[key].selectedRate = null;
      this.persistToLocalStorage();
    },
    persistToLocalStorage() {
      window.localStorage.setItem('_cke-state-vue', JSON.stringify({
        localLineItems: this.localLineItems,
        customer: this.customer,
        discountData: this.discountData,
      }));
    },
  },
});
